/// <amd-module name="Test/ManageTestPlans/Components/NewTestPlanPageComponent" />

import * as React from "react";
import { NewTestPlanPageActionsCreator } from "Test/ManageTestPlans/Actions/NewTestPlanPageActionsCreator";
import { NewTestPlanPageActionsHub } from "Test/ManageTestPlans/Actions/NewTestPlanPageActionsHub";
import { INewTestPlanPageState, INode, INodeField, IValidationResult } from "Test/ManageTestPlans/NewTestPlanPageContracts";
import * as Resources from "Test/ManageTestPlans/Resources";
import { NewTestPlanPageStore } from "Test/ManageTestPlans/Stores/NewTestPlanPageStore";
import { TestPlansHubSettingsService } from "Test/ManageTestPlansCommon/SettingsService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { LoadingComponent } from "./LoadingComponent";
import { NewTestPlanFormValidators } from "./NewTestPlanFormValidators";

export interface INewTestPlanPageProps {
    onCancel?: () => void;

    onCompleted?: (name: string, projectId: string, areaPath: string, iteration: string) => Promise<void>;
}

export class NewTestPlanPageComponent extends VssComponent<INewTestPlanPageProps, INewTestPlanPageState, IVssComponentContext> {
    private _pageContext: IVssPageContext;
    private _store: NewTestPlanPageStore;
    private _actionCreator: NewTestPlanPageActionsCreator;

    constructor(props: INewTestPlanPageProps, context: IVssComponentContext) {
        super(props);
        this._pageContext = context.pageContext;
        this._actionCreator = new NewTestPlanPageActionsCreator(NewTestPlanPageActionsHub.getInstance());
        this._store = new NewTestPlanPageStore(NewTestPlanPageActionsHub.getInstance());
        this._actionCreator.initialize(this._pageContext);
        this.state = this._store.getNewPlanState();
    }

    public componentWillMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element {
        const {
            isLoading,
            nameField,
            rootAreaPath,
            selectedAreaPathField,
            rootIteration,
            selectedIterationField,
            projectId,
            isCreatingTestPlan,
            errorMessage
        } = this.state;

        if (isLoading) {
            return (
                <div className="new-test-plan-loading">
                    <LoadingComponent />
                </div>
            );
        }

        const disableSubmit = !this._isFormValid();

        return (
            <div className="new-test-plan-page">
                {errorMessage && this._renderErrorMessage(errorMessage)}
                <h1 className="new-test-plan-page-title" key="title">
                    <VssIcon iconType={VssIconType.bowtie} iconName="test-plan" styles={{ root: { color: "#004b50" } }} />
                    <span className="new-test-plan-page-title-header">{Resources.NewTestPlanText}</span>
                </h1>

                <form onSubmit={this._onFormSubmitted}>
                    <div className="test-plan-name" key="name">
                        <TextField
                            label={Resources.Name}
                            required={true}
                            autoComplete={false}
                            value={nameField.value}
                            placeholder={Resources.NewTestPlanPlaceholder}
                            onChange={(e, newValue) => {
                                this._onNameFieldChanged(newValue);
                            }}
                            autoFocus={true}
                        />
                    </div>

                    <div className="test-plan-area-path" key="area-path">
                        {this._renderNodeSection(rootAreaPath, rootIteration)}
                    </div>

                    <div className="submit-buttons-container">
                        {isCreatingTestPlan && <Spinner />}
                        <Button disabled={disableSubmit} type="submit" className="new-test-plan-submit" primary={true}>
                            {Resources.CreateButtonText}
                        </Button>

                        <Button onClick={this._onCancel} className="new-sprint-form-cancel">
                            {Resources.CancelText}
                        </Button>
                    </div>
                </form>
            </div>
        );
    }

    private _renderErrorMessage(message: string): JSX.Element {
        return (
            <MessageBar
                severity={MessageBarSeverity.Error}
                onDismiss={() => {
                    this.setState({ errorMessage: "" });
                }}
            >
                {message}
            </MessageBar>
        );
    }

    private _onFormSubmitted = (formEvent: React.FormEvent<HTMLFormElement>) => {
        // Stop the browser from POSTing back by default
        formEvent.preventDefault();
        formEvent.stopPropagation();

        if (this._isFormValid()) {
            const { nameField, selectedAreaPathField, selectedIterationField, projectId } = this.state;

            this._onCompleted(nameField.value, projectId, selectedAreaPathField.value.path, selectedIterationField.value.path);
        }
    };

    private _onCompleted = (name: string, projectId: string, areaPath: string, iteration: string) => {
        return this._actionCreator.createTestPlan(name, projectId, areaPath, iteration).then((id: number) => {});
    };

    private _renderNodeSection(rootAreaPath: INodeField, rootIteration: INodeField): JSX.Element {
        return (
            <LegacyComponent
                modules={["TestManagement/Scripts/Shared/AreaPathAndIterationPicker"]}
                wrappedType="area-path-and-iteration-picker"
                wrappedProps={{
                    areaPathNode: rootAreaPath.node,
                    iterationNode: rootIteration.node,
                    projectId: this.state.projectId,
                    areaPathNodeSelected: this._onAreaPathNodeChanged.bind(this),
                    iterationNodeSelected: this._onIterationNodeChanged.bind(this),
                    selectedAreaPath: this.state.selectedAreaPathField.value.path,
                    selectedIteration: this.state.selectedIterationField.value.path,
                    areaPathMruItemIds: this.state.areaPathMruIds,
                    iterationMruItemIds: this.state.iterationMruIds,
                    asteriskRequired: true
                }}
            />
        );
    }

    private _onAreaPathNodeChanged = (value: string, node: INode) => {
        const validationResult: IValidationResult = NewTestPlanFormValidators.validateNodeValue(value, this.state.rootAreaPath.node);
        this.setState({
            selectedAreaPathField: {
                value: {
                    path: value,
                    node: node
                },
                validationResult,
                changed: true
            }
        });
    };

    private _onIterationNodeChanged = (value: string, node: INode) => {
        const validationResult: IValidationResult = NewTestPlanFormValidators.validateNodeValue(value, this.state.rootIteration.node);
        this.setState({
            selectedIterationField: {
                value: {
                    path: value,
                    node: node
                },
                validationResult,
                changed: true
            }
        });
    };

    private _onStoreChanged = () => {
        if (this.state.nameField.value === "" || this.state.nameField.value === undefined) {
            this.setState(this._store.getNewPlanState());
        } else {
            this.setState({
                nameField: {
                    value: this.state.nameField.value,
                    validationResult: { isValid: false }
                },
                isCreatingTestPlan: this._store.isCreatingTestPlan
            });
        }
    };

    private _onNameFieldChanged = (value: string) => {
        const { nameField } = this.state;

        if (nameField.value !== value) {
            const validationResult: IValidationResult = NewTestPlanFormValidators.validateName(value);
            this.setState({
                nameField: {
                    value,
                    validationResult,
                    changed: true
                }
            });
        }
    };

    private _isFormValid(): boolean {
        return (
            this.state.nameField.validationResult.isValid &&
            this.state.selectedAreaPathField.validationResult.isValid &&
            this.state.selectedIterationField.validationResult.isValid
        );
    }

    private _onCancel = () => {
        const service = this._pageContext.getService<TestPlansHubSettingsService>("ITestPlanHubSettingService");
        const tfsPageService: ITfsPageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let project = "";
        if (tfsPageData && tfsPageData.project) {
            project = tfsPageData.project.name;
        }
        const routeValues: { [key: string]: string } = {
            pivot: service.userOptions.selectedPivot,
            project: project
        };
        const locationService: IVssLocationService = this._pageContext.getService<IVssLocationService>("IVssLocationService");
        const mineAllPageUrl = locationService.routeUrl("ms.vss-test-web.test-mine-directory-route-LWP", routeValues);
        const openedWindow = window.open(mineAllPageUrl, "_self");
        if (openedWindow) {
            openedWindow.opener = null;
        }
    };
}

VssComponent.register("manageNewTestPlanPage", NewTestPlanPageComponent);
